/**
 * The layout of a frame's notes box, shared by the editor (ShotNotes) and
 * the page renderer so the printed board matches what you typed.
 *
 *   ┌───────────────────────────────────────┐
 *   │ SHOT 4        MS · Push in · 3s       │  header
 *   │ ACTION                                │
 *   │ She steps into the light…             │  action
 *   │ DIALOGUE / SOUND                      │
 *   │ "You came back."                      │  dialogue (not on compact boards)
 *   └───────────────────────────────────────┘
 */

export const NOTES_FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";
export const NOTES_INK = "#1a1a1a";
export const NOTES_MUTED = "#6b665d";
export const NOTES_RULE = "rgba(26, 26, 26, 0.55)";
export const NOTES_PADDING = 5;
export const NOTES_HEADER_HEIGHT = 18;
export const NOTES_LABEL_SIZE = 7.5;
export const NOTES_LABEL_HEIGHT = 11;
export const NOTES_TEXT_SIZE = 10.5;
export const NOTES_LINE_HEIGHT = 1.3;
// Share of the body given to action when dialogue is shown too
const ACTION_SHARE = 0.55;

/** Header summary like "MS · Push in · 3s". */
export const shotSummary = (shot = {}) =>
  [shot.shotType, shot.move, shot.duration > 0 ? `${shot.duration}s` : null]
    .filter(Boolean)
    .join(" · ");

/**
 * Sections of a notes box, relative to the box's top-left.
 * @returns {{header, sections: Array<{field, label, x, y, width, height, textY, textHeight}>}}
 */
export const notesLayout = (box, { compact = false } = {}) => {
  const innerX = NOTES_PADDING;
  const innerW = Math.max(1, box.width - NOTES_PADDING * 2);
  const bodyTop = NOTES_HEADER_HEIGHT;
  const bodyH = Math.max(0, box.height - bodyTop - NOTES_PADDING / 2);
  const fields = compact
    ? [{ field: "action", label: "Action", height: bodyH }]
    : [
      { field: "action", label: "Action", height: bodyH * ACTION_SHARE },
      { field: "dialogue", label: "Dialogue / sound", height: bodyH * (1 - ACTION_SHARE) },
    ];
  let y = bodyTop;
  const sections = fields.map((f) => {
    // Compact boards skip the label: the box is only a line or two tall
    const labelH = compact ? 0 : NOTES_LABEL_HEIGHT;
    const section = {
      field: f.field,
      label: f.label,
      showLabel: !compact,
      x: innerX,
      y,
      width: innerW,
      height: f.height,
      textY: y + labelH,
      textHeight: Math.max(0, f.height - labelH),
    };
    y += f.height;
    return section;
  });
  return {
    header: { x: innerX, y: 0, width: innerW, height: NOTES_HEADER_HEIGHT },
    sections,
  };
};
